// USPS-standardize a destination before buying a label (SERA address validation).
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { MapPin } from 'lucide-react'
import { API_BASE } from '../../../lib/api'
import { Card } from '../../../components/ui/card'
import { useT } from '../../../i18n'
import { shippingProvidersI18n } from '../../../i18n/admin/shipping-providers'

type AddressCandidate = {
  full_name: string | null
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string
}
type ValidationResult = {
  valid: boolean
  normalized: AddressCandidate | null
  candidates: AddressCandidate[]
  messages: string[]
}

export function AddressCheckCard() {
  const t = useT(shippingProvidersI18n).address
  const [form, setForm] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
  })
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const validate = useMutation({
    mutationFn: async (): Promise<ValidationResult> => {
      const res = await fetch(`${API_BASE}/admin/shipping/validate-address`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...form, country: 'US' }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })
  const r = validate.data

  return (
    <Card className="p-5 space-y-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
          <MapPin className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{t.title}</h2>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {t.hint}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.line1} onChange={set('line1')} placeholder={t.street} className="col-span-2 px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-400" />
        <input value={form.city} onChange={set('city')} placeholder={t.city} className="px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-400" />
        <input value={form.state} onChange={set('state')} placeholder={t.state} className="px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-400" />
        <input value={form.postal_code} onChange={set('postal_code')} placeholder={t.zip} className="px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-400" />
      </div>
      <button
        onClick={() => validate.mutate()}
        disabled={validate.isPending || !form.line1.trim() || !form.postal_code.trim()}
        className="px-3 py-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-50"
      >
        {validate.isPending ? t.validating : t.validate}
      </button>
      {validate.isError && (
        <p className="text-xs text-rose-600">{t.unavailable}</p>
      )}
      {r && (
        <div className={`rounded-md border p-2 text-xs ${r.valid ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
          <p className={`font-medium ${r.valid ? 'text-emerald-700' : 'text-amber-700'}`}>
            {r.valid ? t.deliverable : t.notConfirmed}
          </p>
          {r.normalized && (
            <p className="mt-1 text-slate-700">
              {[r.normalized.line1, r.normalized.city, r.normalized.state, r.normalized.postal_code]
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
          {r.messages.length > 0 && (
            <p className="mt-1 text-slate-500">{r.messages.join(' · ')}</p>
          )}
        </div>
      )}
    </Card>
  )
}

